import { Badge } from "@package/subframe/components/Badge";
import { Button } from "@package/subframe/components/Button";
// import { IconButton } from "@package/subframe/components/IconButton";
import { Table } from "@package/subframe/components/Table";
// import { FeatherMoreHorizontal } from "@subframe/core";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { format } from "date-fns";
import { useEffect, useMemo, useState } from "react";

import { Pagination } from "@/components/ui/pagination";
import { Spinner } from "@/components/ui/spinner";
import {
  TABLE_MIN_WIDTH,
  TableScrollContainer,
} from "@/components/ui/table-scroll-container";
import { useNursery } from "@/contexts/NurseryContext";
import { useOrganisation } from "@/contexts/OrganisationContext";
import {
  AddQuestionnaireConfigDtoAssigneeRolesItem,
  NurseryConfigResponseDto,
  NurseryConfigResponseDtoFrequency,
  NurseryConfigResponseDtoStatus,
  TemplateListItemResponseDto,
} from "@/generated/model";
import { GetTemplatesByOrganisationQueryControllerGetTemplatesByOrganisationStatus } from "@/generated/model/getTemplatesByOrganisationQueryControllerGetTemplatesByOrganisationStatus";
import { useGetTemplatesByOrganisationQueryControllerGetTemplatesByOrganisation } from "@/generated/questionnaire-templates/questionnaire-templates";

export const Route = createFileRoute("/_authenticated/templates/")({
  component: TemplatesPage,
});

type ConfigStatus = NurseryConfigResponseDtoStatus | "NOT_IN_USE";

interface TemplateTableRow {
  id: string;
  name: string;
  questionCount: number;
  scheduleLabel: string;
  scheduleDetail: string;
  assignedTo: string;
  dueBy: string;
  status: ConfigStatus;
  isScheduled: boolean;
}

const STATUS_BADGE_VARIANT: Record<
  ConfigStatus,
  "success" | "neutral" | "warning" | "error" | "brand"
> = {
  NOT_IN_USE: "neutral",
  DRAFT: "neutral",
  PUBLISHED: "success",
  PAUSED: "warning",
};

const STATUS_DISPLAY: Record<ConfigStatus, string> = {
  NOT_IN_USE: "Not in use",
  DRAFT: "Not in use",
  PUBLISHED: "Published",
  PAUSED: "Paused",
};

const DAY_MAP: Record<string, string> = {
  MO: "Mon",
  TU: "Tue",
  WE: "Wed",
  TH: "Thu",
  FR: "Fri",
  SA: "Sat",
  SU: "Sun",
};

function formatTime(time?: string | null): string {
  if (!time) return "N/A";
  if (time === "23:59" || time === "23:59:59") return "End of day";

  const parts = time.split(":");
  const hours = parts[0] || "0";
  const minutes = parts[1] || "00";
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? "PM" : "AM";
  const hour12 = hour % 12 || 12;

  return `${hour12}:${minutes} ${ampm}`;
}

function getScheduleDisplay(config: NurseryConfigResponseDto | undefined): {
  label: string;
  detail: string;
  dueBy: string;
  isScheduled: boolean;
} {
  if (!config)
    return {
      label: "Schedule not set",
      detail: "",
      dueBy: "N/A",
      isScheduled: false,
    };

  const freq = config.frequency;

  if (freq === NurseryConfigResponseDtoFrequency.ONE_TIME) {
    const start = config.activeStartDate
      ? format(new Date(config.activeStartDate), "d MMM yyyy")
      : "";
    const end = config.activeEndDate
      ? format(new Date(config.activeEndDate), "d MMM yyyy")
      : "";
    return {
      label: "One-time",
      detail: start && end ? `${start} – ${end}` : start || "",
      dueBy: end
        ? `${end}, ${formatTime(config.endTime)}`
        : formatTime(config.endTime),
      isScheduled: true,
    };
  }

  if (freq === NurseryConfigResponseDtoFrequency.MONTHLY) {
    const fromDay = config.availableFromDayOfMonth ?? "?";
    const dueDay = config.dueByDayOfMonth ?? "?";
    return {
      label: "Monthly",
      detail: `Day ${fromDay} to day ${dueDay}`,
      dueBy: `Day ${dueDay}, ${formatTime(config.endTime)}`,
      isScheduled: true,
    };
  }

  if (freq === NurseryConfigResponseDtoFrequency.DAILY) {
    return {
      label: "Daily",
      detail: "Every day",
      dueBy: formatTime(config.endTime),
      isScheduled: true,
    };
  }

  // WEEKLY or legacy (infer from scheduleRule)
  if (config.scheduleRule) {
    const bydayMatch = config.scheduleRule.match(/BYDAY=([A-Z,]+)/);
    const bydayStr = bydayMatch?.[1] || "";
    if (bydayStr) {
      const days = bydayStr.split(",");
      const allWeekdays = ["MO", "TU", "WE", "TH", "FR"];
      const allDays = ["MO", "TU", "WE", "TH", "FR", "SA", "SU"];
      const isAllWeekdays =
        days.length === 5 && allWeekdays.every((d) => days.includes(d));
      const isAllDays =
        days.length === 7 && allDays.every((d) => days.includes(d));

      let daysDisplay = days.map((d) => DAY_MAP[d] || d).join(", ");
      if (isAllWeekdays) daysDisplay = "Mon–Fri";
      if (isAllDays) daysDisplay = "Every day";

      return {
        label: "Weekly",
        detail: daysDisplay,
        dueBy: formatTime(config.endTime),
        isScheduled: true,
      };
    }
  }

  return {
    label: "Schedule not set",
    detail: "",
    dueBy: "N/A",
    isScheduled: false,
  };
}

function formatAssignees(roles?: string[], userIds?: string[]): string {
  // If assigneeUserIds is present, show the count of team members
  if (userIds && userIds.length > 0) {
    return `${userIds.length} team member${userIds.length > 1 ? "s" : ""}`;
  }

  if (!roles || roles.length === 0) return "Not assigned";

  const roleMap: Record<string, string> = {
    [AddQuestionnaireConfigDtoAssigneeRolesItem.MANAGER]: "Manager",
    [AddQuestionnaireConfigDtoAssigneeRolesItem.DEPUTY_MANAGER]:
      "Deputy Manager",
    [AddQuestionnaireConfigDtoAssigneeRolesItem.LEADER]: "Leader",
    [AddQuestionnaireConfigDtoAssigneeRolesItem.PRACTITIONER]: "Practitioner",
  };

  return roles.map((r) => roleMap[r] || r).join(", ");
}

function getConfigStatus(
  status: NurseryConfigResponseDtoStatus | undefined,
): ConfigStatus {
  if (!status) return "NOT_IN_USE";
  return status;
}

const PAGE_SIZE = 10;

export function TemplatesPage() {
  const navigate = useNavigate();
  const { selectedNurseryId } = useNursery();
  const { activeOrg, activeOrgId } = useOrganisation();
  const [page, setPage] = useState(1); // 1-based, matches the Pagination component
  const [pageSize, setPageSize] = useState(PAGE_SIZE);

  const orgId = activeOrg?.id || activeOrgId || undefined;

  const {
    data: templatesData,
    isLoading,
    refetch,
  } = useGetTemplatesByOrganisationQueryControllerGetTemplatesByOrganisation(
    {
      organisationId: orgId,
      status:
        GetTemplatesByOrganisationQueryControllerGetTemplatesByOrganisationStatus.PUBLISHED,
      // limit: PAGE_SIZE,
    },
    { query: { enabled: !!orgId } },
  );

  useEffect(() => {
    if (selectedNurseryId && activeOrgId) {
      refetch();
    }
  }, [selectedNurseryId, activeOrgId, refetch]);

  const templates: TemplateListItemResponseDto[] =
    (templatesData as { data?: TemplateListItemResponseDto[] } | undefined)
      ?.data ?? [];

  const tableData: TemplateTableRow[] = templates.map((template) => {
    const config = template.nurseryConfigs?.find(
      (c) => c.nurseryId === selectedNurseryId,
    );
    const schedule = getScheduleDisplay(config);

    return {
      id: template.id,
      name: template.name,
      questionCount: template.questionCount ?? template.questions?.length ?? 0,
      scheduleLabel: schedule.label,
      scheduleDetail: schedule.detail,
      assignedTo: formatAssignees(
        config?.assigneeRoles,
        config?.assigneeUserIds,
      ),
      dueBy: schedule.dueBy,
      status: getConfigStatus(config?.status),
      isScheduled: schedule.isScheduled,
    };
  });

  // Client-side paging: the templates endpoint returns the full org set, so
  // slice it locally for the current page.
  const pagedRows = useMemo(
    () => tableData.slice((page - 1) * pageSize, page * pageSize),
    [tableData, page, pageSize],
  );

  return (
    <div className="flex h-full w-full flex-col items-start gap-8 bg-white animate-fade-in">
      {/* Header Section */}
      <div className="flex w-full flex-col items-start gap-1">
        <div className="flex w-full flex-wrap items-center gap-4">
          <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
            <span className="w-full whitespace-nowrap text-metrics font-metrics text-primary-navy-800">
              All templates
            </span>
            <span className="text-body font-body text-subtext-color">
              These templates are based on best-practice nursery operations. You
              can use them without editing anything.
            </span>
          </div>
        </div>
      </div>

      {/* Table Section */}
      <div className="flex w-full flex-col items-start gap-4 pb-8">
        {isLoading ? (
          <div className="flex w-full items-center justify-center py-12">
            <Spinner className="h-8 w-8 text-primary" />
          </div>
        ) : templates.length === 0 ? (
          <div className="flex w-full items-center justify-center py-12">
            <span className="text-body font-body text-subtext-color">
              No templates available
            </span>
          </div>
        ) : (
          <>
            <TableScrollContainer>
              <Table
                className={TABLE_MIN_WIDTH.md}
                header={
                  <Table.HeaderRow>
                    <Table.HeaderCell>Template name</Table.HeaderCell>
                    <Table.HeaderCell>Schedule</Table.HeaderCell>
                    <Table.HeaderCell>Due by</Table.HeaderCell>
                    <Table.HeaderCell>Assigned to</Table.HeaderCell>
                    <Table.HeaderCell>Status</Table.HeaderCell>
                    <Table.HeaderCell variant="aligned-right">
                      Actions
                    </Table.HeaderCell>
                  </Table.HeaderRow>
                }
              >
                {pagedRows.map((row) => (
                  <Table.Row key={row.id} clickable={true}>
                    <Table.Cell className="h-14 grow shrink-0 basis-0">
                      <div className="flex flex-col items-start">
                        <span className="text-body-bold font-body-bold text-neutral-900">
                          {row.name}
                        </span>
                        <span className="text-caption font-caption text-subtext-color">
                          {row.questionCount} questions
                        </span>
                      </div>
                    </Table.Cell>
                    <Table.Cell className="h-14 grow shrink-0 basis-0">
                      {row.isScheduled ? (
                        <div className="flex flex-col items-start">
                          <span className="text-caption font-caption text-subtext-color">
                            {row.scheduleLabel}
                          </span>
                          {row.scheduleDetail && (
                            <span className="text-caption-bold font-caption-bold text-subtext-color">
                              {row.scheduleDetail}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-caption font-caption text-subtext-color">
                          Schedule not set
                        </span>
                      )}
                    </Table.Cell>
                    <Table.Cell className="h-14 grow shrink-0 basis-0">
                      <span
                        className={`text-caption font-body ${row.isScheduled ? "text-neutral-900" : "text-subtext-color"}`}
                      >
                        {row.dueBy}
                      </span>
                    </Table.Cell>
                    <Table.Cell className="h-14 grow shrink-0 basis-0">
                      {row.isScheduled ? (
                        <Badge variant="neutral">{row.assignedTo}</Badge>
                      ) : (
                        <span className="text-caption font-caption text-subtext-color">
                          Not assigned
                        </span>
                      )}
                    </Table.Cell>
                    <Table.Cell className="h-14 grow shrink-0 basis-0">
                      <Badge variant={STATUS_BADGE_VARIANT[row.status]}>
                        {STATUS_DISPLAY[row.status]}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell
                      className="h-14 grow shrink-0 basis-0"
                      variant="aligned-right"
                    >
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="neutral-primary"
                          onClick={() =>
                            navigate({ to: `/templates/${row.id}/schedule` })
                          }
                        >
                          View
                        </Button>
                        {/*<IconButton*/}
                        {/*  variant="neutral-primary"*/}
                        {/*  icon={<FeatherMoreHorizontal />}*/}
                        {/*/>*/}
                      </div>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table>
            </TableScrollContainer>

            <Pagination
              page={page}
              pageSize={pageSize}
              totalCount={tableData.length}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
              itemNoun="templates"
            />
          </>
        )}
      </div>
    </div>
  );
}
